import { Api } from "@penn/web-common";
import { unwrap } from "@penn/web-common";

// =============================================================================
// Approval workflow (svc-07, via gateway /v1/approvals). Mirrors
// packages/shared-types/src/svc-07-approval-workflow/v1/approval.schema.ts.
//
// MD-05 two-flow model: the `engineer` flow runs Engineering Review
// (`can_approve_engineering`) → Sales Sign-off; the `sales` flow is a single
// Sales Sign-off that auto-approves for the creator. svc-06 hard-gates
// `POST /v1/tasks/:id/quote` (409 `awaiting-approval`) until `complete`.
// =============================================================================

export type ApprovalFlowType = "engineer" | "sales";
export type ApprovalDecision = "approve" | "reject";

export interface ApprovalAssignment {
  assignmentId: string;
  taskId: string;
  stepId: string;
  stepOrder: number;
  stepName: string;
  requiredRoleCode: string | null;
  assigneeUserId: string | null;
  assignedAt: string;
  decidedAt: string | null;
  decision: "approve" | "reject" | "auto_approve" | null;
  decisionComment: string | null;
}

export interface ApprovalAuditEntry {
  id: string;
  taskId: string;
  flowType: ApprovalFlowType;
  eventType: string;
  fromStatus: string | null;
  toStatus: string;
  stepName: string | null;
  stepOrder: number | null;
  actorUserId: string | null;
  actorEmail: string;
  actorName: string | null;
  actorRoleCode: string | null;
  decisionReason: string | null;
  occurredAt: string;
}

export interface ApprovalState {
  taskId: string;
  flowType: ApprovalFlowType;
  /** Every step decided approve/auto_approve. svc-06 gates the quote on this. */
  complete: boolean;
  /** Any step decided reject. */
  rejected: boolean;
  /** First un-decided assignment, or null when complete/rejected. */
  currentAssignment: ApprovalAssignment | null;
  assignments: ApprovalAssignment[];
  /** Append-only audit log, newest first. */
  audit: ApprovalAuditEntry[];
}

export interface ApprovalDecisionInput {
  decision: ApprovalDecision;
  comment?: string | null;
}

/**
 * GET /v1/approvals/tasks/:taskId — the task's approval state. Returns null on
 * a 404 (no flow has been started for this task) so callers can render a clean
 * "not started" state instead of erroring.
 */
export async function getApprovalState(
  taskId: string,
): Promise<ApprovalState | null> {
  const res = await Api.get(
    `/v1/approvals/tasks/${encodeURIComponent(taskId)}`,
    { validateStatus: (s) => (s >= 200 && s < 300) || s === 404 },
  );
  if (res.status === 404) return null;
  return unwrap<ApprovalState>(res.data);
}

/** POST /v1/approvals/tasks/:taskId/decide — approve/reject the current step. */
export async function decideApproval(
  taskId: string,
  body: ApprovalDecisionInput,
): Promise<ApprovalState> {
  const res = await Api.post(
    `/v1/approvals/tasks/${encodeURIComponent(taskId)}/decide`,
    body,
  );
  return unwrap<ApprovalState>(res.data);
}
